import { useMemo, type SyntheticEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Combobox,
  ComboboxActions,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxItemCheckbox,
  ComboboxList,
  ComboboxSearch,
  ComboboxSelectTrigger,
  InputHelperText,
  InputLabel,
  InputRoot,
} from '@mint-lib/tw-components';

import type { OptionType } from '../../types/OptionType.js';

type Value = OptionType['value'];

type SelectFieldProps = {
  id: string;
  label: string;
  placeholder?: string;
  options: OptionType[];
  disabled?: boolean;
  infoText?: string;
  helperText?: string;
  error?: boolean;
  size?: 'small' | 'medium';
  emptyMessage?: string;
  className?: string;
  /** Forwarded unchanged to the trigger, Select all and every option. */
  'data-dd-action-name'?: string;
} & (
  | {
      multiple?: false;
      value: Value;
      onChange: (value: Value) => void;
    }
  | {
      multiple: true;
      value: Value[];
      onChange: (value: Value[]) => void;
    }
);

// TODO: remove once ComboboxContent can portal into the dialog it is opened from
const keepInsideDialog = (event: SyntheticEvent) => event.stopPropagation();

const itemToStringLabel = (option: OptionType) => option.label;
const isItemEqualToValue = (option: OptionType, selected: OptionType) =>
  option.value === selected.value;

// Dropdown with a search box inside it, built from the tw-components combobox. `multiple` switches it to checkboxes with Select all.
const SelectField = (props: SelectFieldProps) => {
  const {
    id,
    label,
    placeholder,
    options,
    disabled,
    infoText,
    helperText,
    error,
    size,
    emptyMessage,
    className,
    'data-dd-action-name': ddActionName,
  } = props;
  const { t } = useTranslation('@myn/permissions');

  // The combobox works with the option objects; callers keep passing the values.
  const selectedOptions = useMemo(() => {
    const selected = new Set(
      (props.multiple ? props.value : [props.value]).map(String),
    );
    return options.filter((option) => selected.has(String(option.value)));
  }, [options, props.multiple, props.value]);

  const isAllSelected =
    options.length > 0 && selectedOptions.length === options.length;

  const color = error ? 'error' : 'default';

  const content = (
    <>
      <ComboboxSelectTrigger
        id={id}
        placeholder={placeholder}
        size={size}
        color={color}
        disabled={disabled}
        className="min-w-0"
        onBlur={keepInsideDialog}
        data-dd-action-name={ddActionName}
      />
      <ComboboxContent
        onFocus={keepInsideDialog}
        onBlur={keepInsideDialog}
        onWheel={keepInsideDialog}
        onTouchMove={keepInsideDialog}
      >
        <ComboboxSearch placeholder={t('Search')} />
        {props.multiple && options.length > 0 ? (
          <ComboboxActions>
            <Button
              type="button"
              variant="plain"
              data-dd-action-name={ddActionName}
              onClick={() => {
                props.onChange(
                  isAllSelected ? [] : options.map((option) => option.value),
                );
              }}
            >
              {isAllSelected ? t('Clear all') : t('Select all')}
            </Button>
          </ComboboxActions>
        ) : null}
        <ComboboxEmpty>{emptyMessage ?? t('No options')}</ComboboxEmpty>
        <ComboboxList>
          {(option: OptionType) => (
            <ComboboxItem
              key={option.value}
              value={option}
              data-dd-action-name={ddActionName}
              className={
                props.multiple ? undefined : 'grid-cols-[minmax(0,1fr)_auto]'
              }
            >
              {props.multiple ? <ComboboxItemCheckbox /> : null}
              <span className="truncate">{option.label}</span>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </>
  );

  return (
    <InputRoot className={className ?? 'w-full'}>
      <InputLabel label={label} htmlFor={id} infoIconWithTooltip={infoText} />
      {props.multiple ? (
        <Combobox<OptionType, true>
          items={options}
          itemToStringLabel={itemToStringLabel}
          isItemEqualToValue={isItemEqualToValue}
          multiple
          value={selectedOptions}
          onValueChange={(next) => {
            props.onChange(next.map((option) => option.value));
          }}
          disabled={disabled}
        >
          {content}
        </Combobox>
      ) : (
        <Combobox<OptionType>
          items={options}
          itemToStringLabel={itemToStringLabel}
          isItemEqualToValue={isItemEqualToValue}
          value={selectedOptions[0] ?? null}
          onValueChange={(option) => {
            if (option) {
              props.onChange(option.value);
            }
          }}
          disabled={disabled}
        >
          {content}
        </Combobox>
      )}
      {helperText ? (
        <InputHelperText helperText={helperText} color={color} />
      ) : null}
    </InputRoot>
  );
};

export default SelectField;
